import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Sparkles } from 'lucide-react';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import type { PurchaseAnalysisListResponse } from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

/**
 * SIL-1072 — `/market-intelligence` saved-analyses list (design spec §4
 * "The builder" precedes this: the list/New screen). First-run gate
 * (§4 "First-run gate") stays exactly as SIL-1071 shipped it: if the org's
 * `costsConfigured` is false, redirect to cost-settings BEFORE this page
 * mounts, so an org with no house rates never sees an empty builder that
 * would compute a plausible-looking, silently wrong margin.
 */
const CONFIGURED_FETCH_TIMEOUT_MS = 4000;

async function resolveCostsConfigured(): Promise<boolean> {
  const timeout = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('cost-constants lookup timed out')), CONFIGURED_FETCH_TIMEOUT_MS);
  });
  const fetchConfigured = async () => {
    const res = await api.api.client['buyer-cost-constants'].$get();
    await throwIfNotOk(res);
    const data = await res.json();
    return data.costsConfigured;
  };
  return Promise.race([fetchConfigured(), timeout]);
}

export const Route = createFileRoute('/market-intelligence/')({
  beforeLoad: async () => {
    let configured = false; // deterministic fallback -> cost-settings on any failure
    try {
      configured = await resolveCostsConfigured();
    } catch {
      configured = false;
    }
    if (!configured) {
      throw redirect({ to: '/market-intelligence/cost-settings' });
    }
  },
  component: MarketIntelligenceIndexPage,
});

function MarketIntelligenceIndexPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const dataQ = useQuery<PurchaseAnalysisListResponse>({
    queryKey: qk.marketIntel.analyses(),
    queryFn: async () => {
      const res = await api.api.client['purchase-analyses'].$get();
      await throwIfNotOk(res);
      return res.json();
    },
  });

  async function createAnalysis() {
    setCreating(true);
    try {
      const res = await api.api.client['purchase-analyses'].$post({
        json: { name: `Untitled analysis — ${new Date().toLocaleDateString()}`, targetMarginPct: 0.2 },
      });
      await throwIfNotOk(res);
      const created = await res.json();
      void queryClient.invalidateQueries({ queryKey: qk.marketIntel.analyses() });
      void navigate({ to: '/market-intelligence/$analysisId', params: { analysisId: created.analysis.id } });
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader title="Market Intelligence" pinTo="/market-intelligence" />
        <Button type="button" disabled={creating} onClick={createAnalysis}>
          <Plus className="mr-1 size-4" /> {creating ? 'Creating…' : 'New analysis'}
        </Button>
      </div>

      <QueryBoundary query={dataQ} errorTitle="Couldn't load your saved analyses.">
        {(data) =>
          data.rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-white px-6 py-16 text-center">
              <Sparkles className="mb-3 size-8 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">No saved analyses yet</p>
              <p className="mt-1 max-w-sm text-xs text-muted-foreground">
                Start a purchase analysis to price a bid against your house rates and the forecast band.
              </p>
              <Button type="button" disabled={creating} onClick={createAnalysis} className="mt-4">
                <Plus className="mr-1 size-4" /> New analysis
              </Button>
            </div>
          ) : (
            <Card as="section" className="divide-y divide-border p-0">
              {data.rows.map((row) => (
                <Link
                  key={row.id}
                  to="/market-intelligence/$analysisId"
                  params={{ analysisId: row.id }}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted"
                >
                  <div>
                    <p className="text-sm font-medium text-foreground">{row.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Target margin {(row.targetMarginPct * 100).toFixed(0)}% · Updated{' '}
                      {new Date(row.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </Link>
              ))}
            </Card>
          )
        }
      </QueryBoundary>
    </div>
  );
}
